import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Coins, Copy, Save, Trash2, AlertTriangle, ChevronDown } from 'lucide-react';
import { format, parseISO, startOfWeek, subDays } from 'date-fns';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

// Weekly tip distribution: bakery share, the rest by tracked hours of regular
// shifts. Admins, superadmins and staff with the "Tip calculator" permission.

const euro = (cents) => `${(cents / 100).toFixed(2)} €`;
const fmtDay = (d) => format(parseISO(d), 'EEE d MMM');

// Last full Mon–Sun before this week
export function lastWeekRange(today = new Date()) {
  const monday = startOfWeek(today, { weekStartsOn: 1 });
  return { start: format(subDays(monday, 7), 'yyyy-MM-dd'), end: format(subDays(monday, 1), 'yyyy-MM-dd') };
}

async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older browsers / non-https: select a hidden textarea
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
}

function BakeryShare({ settings, onSaved }) {
  const [value, setValue] = useState(String(settings.bakery_percent));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setValue(String(settings.bakery_percent)); }, [settings.bakery_percent]);

  if (!settings.can_edit) {
    return <p style={{ margin: 0, fontSize: '0.9rem' }}>Bakery share: <strong>{settings.bakery_percent} %</strong></p>;
  }
  const changed = value.replace(',', '.') !== String(settings.bakery_percent);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put('/tips/settings', { bakery_percent: value });
      onSaved(r.data.bakery_percent);
      toast.success('Bakery share saved – the new default for everyone.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      <label htmlFor="bakery-percent" style={{ fontSize: '0.9rem' }}>Bakery share</label>
      <input id="bakery-percent" className="form-input" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)}
        style={{ width: '80px', minHeight: '40px' }} disabled={busy} />
      <span style={{ fontSize: '0.9rem' }}>%</span>
      {changed && (
        <button className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }} onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      )}
    </div>
  );
}

function ResultTable({ r }) {
  const cell = { padding: '8px 6px', borderBottom: '1px solid var(--border)', fontSize: '0.9rem' };
  const num = { ...cell, textAlign: 'right', whiteSpace: 'nowrap' };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: 'var(--text-muted)', fontSize: '0.78rem', textAlign: 'left' }}>
            <th style={{ ...cell, fontWeight: 600, fontSize: '0.78rem' }}>Person</th>
            <th style={{ ...num, fontWeight: 600, fontSize: '0.78rem' }}>Hours</th>
            <th style={{ ...num, fontWeight: 600, fontSize: '0.78rem' }}>Share</th>
            <th style={{ ...num, fontWeight: 600, fontSize: '0.78rem' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ ...cell, fontWeight: 600 }}>Bakery</td>
            <td style={num} />
            <td style={num}>{r.bakery_percent} %</td>
            <td style={{ ...num, fontWeight: 600 }}>{euro(r.bakery_cents)}</td>
          </tr>
          {r.people.map((p) => (
            <tr key={p.user_id}>
              <td style={cell}>{p.name}</td>
              <td style={num}>{p.hours.toFixed(2)}</td>
              <td style={num}>{p.share.toFixed(1)} %</td>
              <td style={{ ...num, fontWeight: 600 }}>{euro(p.amount_cents)}</td>
            </tr>
          ))}
          <tr style={{ color: 'var(--text-light)' }}>
            <td style={cell}>Remainder (rounding)</td>
            <td style={num} />
            <td style={num} />
            <td style={num}>{euro(r.remainder_cents)}</td>
          </tr>
          <tr>
            <td style={{ ...cell, fontWeight: 700, borderBottom: 'none' }}>Total</td>
            <td style={{ ...num, borderBottom: 'none' }}>{(r.total_minutes / 60).toFixed(2)}</td>
            <td style={{ ...num, borderBottom: 'none' }} />
            <td style={{ ...num, fontWeight: 700, borderBottom: 'none' }}>{euro(r.total_cents)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function History({ list, canDelete, onDeleted }) {
  const [open, setOpen] = useState(null);
  if (!list.length) return <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', margin: 0 }}>No saved distributions yet.</p>;
  const remove = async (d) => {
    if (!window.confirm(`Delete the distribution ${fmtDay(d.start)} – ${fmtDay(d.end)} (${euro(d.total_cents)})?`)) return;
    try {
      await api.delete(`/tips/distributions/${d.id}`);
      toast.success('Distribution deleted.');
      onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete.');
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {list.map((d) => (
        <div key={d.id} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', background: 'var(--white)' }}>
          <button type="button" onClick={() => setOpen(open === d.id ? null : d.id)} aria-expanded={open === d.id}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '0.9rem', flex: '1 1 160px' }}>{fmtDay(d.start)} – {fmtDay(d.end)}</strong>
            <span style={{ fontSize: '0.9rem' }}>{euro(d.total_cents)}</span>
            <ChevronDown size={15} aria-hidden="true" style={{ transform: open === d.id ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', flexBasis: '100%' }}>
              Saved by {d.created_by_name || '—'}, {format(new Date(d.created_at), 'd MMM yyyy HH:mm')}
              {' · '}{d.rounding === 'half' ? 'rounded to 0.50 €' : 'rounded to the cent'}
            </span>
          </button>
          {open === d.id && (
            <div style={{ padding: '0 12px 12px' }}>
              <ResultTable r={d} />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                <button className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }}
                  onClick={async () => (await copyToClipboard(d.text) ? toast.success('Copied.') : toast.error('Could not copy.'))}>
                  <Copy size={14} /> Copy as text
                </button>
                {canDelete && (
                  <button className="btn btn-danger btn-sm" style={{ minHeight: '40px' }} onClick={() => remove(d)}>
                    <Trash2 size={14} /> Delete
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function TipsPage() {
  const { user, isAdmin } = useAuth();
  const allowed = isAdmin || user?.can_use_tips === 1;
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(() => ({ ...lastWeekRange(), total: '', rounding: 'cent' }));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState('');
  const [history, setHistory] = useState({ distributions: [], can_delete: false });

  const loadHistory = useCallback(() => {
    api.get('/tips/distributions').then((r) => setHistory(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!allowed) return;
    api.get('/tips/settings').then((r) => setSettings(r.data)).catch(() => toast.error('Failed to load the tip settings.'));
    loadHistory();
  }, [allowed, loadHistory]);

  if (!allowed) return <Navigate to="/roster" replace />;

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setResult(null); };

  const calculate = async (e) => {
    e?.preventDefault();
    setBusy('calc');
    try {
      const r = await api.post('/tips/calculate', form);
      setResult(r.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to calculate.');
    } finally {
      setBusy('');
    }
  };

  const save = async () => {
    if (result.overlaps.length && !window.confirm('This range overlaps a saved distribution. Save anyway?')) return;
    setBusy('save');
    try {
      await api.post('/tips/distributions', form);
      toast.success('Distribution saved.');
      loadHistory();
      setResult(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setBusy('');
    }
  };

  const warnBox = { padding: '10px 12px', borderRadius: 'var(--radius)', background: 'var(--warning-light, #FFF8E1)', border: '1px solid var(--warning, #F59E0B)', fontSize: '0.85rem', marginBottom: '12px' };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '16px' }}>
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Coins size={22} /> Tips</h1>
          <p className="page-subtitle">Bakery share first, the rest by hours worked on regular shifts (time tracking).</p>
        </div>
      </div>

      <form className="card" style={{ padding: '16px', marginBottom: '16px' }} onSubmit={calculate}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <div className="form-group" style={{ flex: '1 1 140px', margin: 0 }}>
            <label className="form-label" htmlFor="tips-from">From</label>
            <input id="tips-from" type="date" className="form-input" value={form.start} onChange={set('start')} required />
          </div>
          <div className="form-group" style={{ flex: '1 1 140px', margin: 0 }}>
            <label className="form-label" htmlFor="tips-to">To</label>
            <input id="tips-to" type="date" className="form-input" value={form.end} onChange={set('end')} required />
          </div>
          <div className="form-group" style={{ flex: '1 1 140px', margin: 0 }}>
            <label className="form-label" htmlFor="tips-total">Total tips (€)</label>
            <input id="tips-total" className="form-input" inputMode="decimal" placeholder="e.g. 512,40" value={form.total} onChange={set('total')} required />
          </div>
        </div>
        <fieldset style={{ border: 'none', padding: 0, margin: '12px 0 0' }}>
          <legend className="form-label" style={{ marginBottom: '4px' }}>Rounding (amounts rounded down)</legend>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {[['cent', 'To the cent'], ['half', 'To 0.50 €']].map(([v, l]) => (
              <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', minHeight: '36px', cursor: 'pointer', fontSize: '0.9rem' }}>
                <input type="radio" name="rounding" value={v} checked={form.rounding === v} onChange={set('rounding')} style={{ width: 18, height: 18 }} />
                {l}
              </label>
            ))}
          </div>
        </fieldset>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', marginTop: '12px' }}>
          {settings && <BakeryShare settings={settings} onSaved={(v) => { setSettings((s) => ({ ...s, bakery_percent: v })); setResult(null); }} />}
          <button type="submit" className="btn btn-primary" style={{ minHeight: '44px' }} disabled={busy === 'calc'}>
            {busy === 'calc' ? 'Calculating…' : 'Calculate'}
          </button>
        </div>
      </form>

      {result && (
        <div className="card" style={{ padding: '16px', marginBottom: '16px' }}>
          {result.missing.length > 0 && (
            <div role="alert" style={warnBox}>
              <p style={{ margin: '0 0 6px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <AlertTriangle size={15} /> {result.missing.length} shift{result.missing.length === 1 ? '' : 's'} without a time entry
              </p>
              <p style={{ margin: '0 0 6px' }}>These hours are not counted yet. Better fix them in Time Tracking first – you can still calculate.</p>
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                {result.missing.map((m) => (
                  <li key={`${m.slot_id}-${m.user_id}`}>{m.user_name} · {fmtDay(m.date)} · {m.shift} {m.start_time.slice(0, 5)}–{m.end_time.slice(0, 5)}</li>
                ))}
              </ul>
            </div>
          )}
          {result.overlaps.length > 0 && (
            <div role="alert" style={warnBox}>
              <p style={{ margin: 0, display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This range overlaps {result.overlaps.length === 1 ? 'a saved distribution' : `${result.overlaps.length} saved distributions`}:{' '}
                  {result.overlaps.map((o) => `${fmtDay(o.start_date)} – ${fmtDay(o.end_date)}`).join(', ')}.</span>
              </p>
            </div>
          )}
          <h2 style={{ fontSize: '1rem', margin: '0 0 8px' }}>{fmtDay(result.start)} – {fmtDay(result.end)}</h2>
          {result.people.length === 0 && (
            <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)' }}>No tracked hours on regular shifts in this range.</p>
          )}
          <ResultTable r={result} />
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
            <button className="btn btn-primary" style={{ minHeight: '44px' }} onClick={save} disabled={busy === 'save'}>
              <Save size={15} /> {busy === 'save' ? 'Saving…' : 'Save distribution'}
            </button>
            <button className="btn btn-secondary" style={{ minHeight: '44px' }}
              onClick={async () => (await copyToClipboard(result.text) ? toast.success('Copied – paste it into Telegram.') : toast.error('Could not copy.'))}>
              <Copy size={15} /> Copy as text
            </button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: '16px' }}>
        <h2 style={{ fontSize: '1rem', margin: '0 0 10px' }}>Saved distributions</h2>
        <History list={history.distributions} canDelete={history.can_delete} onDeleted={loadHistory} />
      </div>
    </div>
  );
}
